import { Check } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

export interface ProgressStep {
  id: string;
  label: string;
}

type StepStatus = 'pending' | 'active' | 'done' | 'error';

interface ProgressStepperProps {
  steps: ProgressStep[];
  currentStep: number;
  hasError?: boolean;
  className?: string;
}

function getStepStatus(index: number, currentStep: number, hasError?: boolean): StepStatus {
  if (index < currentStep) return 'done';
  if (index === currentStep) return hasError ? 'error' : 'active';
  return 'pending';
}

export const ProgressStepper: React.FC<ProgressStepperProps> = ({
  steps,
  currentStep,
  hasError = false,
  className,
}) => {
  return (
    <div className={cn('w-full py-4', className)}>
      <div className="flex items-center gap-0">
        {steps.map((step, index) => {
          const status = getStepStatus(index, currentStep, hasError);
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                {/* Circle */}
                <div
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center border-2 transition-all duration-300',
                    status === 'done' && 'bg-primary border-primary',
                    status === 'active' && 'border-primary bg-primary/10 animate-pulse',
                    status === 'error' && 'border-destructive bg-destructive/10',
                    status === 'pending' && 'border-border bg-muted/30',
                  )}
                >
                  {status === 'done' && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                  {status === 'active' && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                  {status === 'error' && (
                    <span className="text-[10px] font-bold text-destructive">!</span>
                  )}
                  {status === 'pending' && (
                    <span className="text-[10px] font-medium text-muted-foreground">{index + 1}</span>
                  )}
                </div>
                {/* Label */}
                <span
                  className={cn(
                    'text-[10px] font-medium whitespace-nowrap',
                    status === 'done' && 'text-primary',
                    status === 'active' && 'text-foreground',
                    status === 'error' && 'text-destructive',
                    status === 'pending' && 'text-muted-foreground',
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 h-0.5 mb-4 mx-1 transition-all duration-500">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      index < currentStep ? 'bg-primary' : 'bg-border',
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressStepper;
